import { useState, useEffect, useCallback, useRef } from 'react';

interface TTSOptions {
  title: string;
  onSentenceChange?: (index: number) => void;
  onComplete?: () => void;
}

interface TTSState {
  isPlaying: boolean;
  currentSentence: number;
  totalSentences: number;
  rate: number;
}

// Split text into sentences
function splitIntoSentences(text: string): string[] {
  // Remove markdown-style formatting
  const cleanText = text
    .replace(/#{1,6}\s/g, '') // Remove headings
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic
    .replace(/`([^`]+)`/g, '$1') // Remove inline code
    .replace(/\n{2,}/g, '\n') // Normalize newlines
    .trim();

  // Split on sentence boundaries
  const sentences = cleanText
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  return sentences;
}

export function useTTS(content: string, options: TTSOptions) {
  const { title, onSentenceChange, onComplete } = options;

  const [state, setState] = useState<TTSState>({
    isPlaying: false,
    currentSentence: 0,
    totalSentences: 0,
    rate: 1.0
  });

  const sentencesRef = useRef<string[]>([]);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Parse sentences on content change
  useEffect(() => {
    sentencesRef.current = splitIntoSentences(content);
    setState(prev => ({
      ...prev,
      currentSentence: 0,
      totalSentences: sentencesRef.current.length
    }));
  }, [content]);

  // Setup Media Session API
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: title,
      artist: 'Read Later',
      album: 'Reading Queue'
    });

    navigator.mediaSession.setActionHandler('play', () => {
      play();
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      pause();
    });

    navigator.mediaSession.setActionHandler('previoustrack', () => {
      skipBackward();
    });

    navigator.mediaSession.setActionHandler('nexttrack', () => {
      skipForward();
    });

    return () => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('previoustrack', null);
      navigator.mediaSession.setActionHandler('nexttrack', null);
    };
  }, [title]);

  // Acquire/release wake lock based on playback state
  useEffect(() => {
    async function acquireWakeLock() {
      if (state.isPlaying && 'wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch (err) {
          console.log('Wake Lock not available:', err);
        }
      }
    }

    async function releaseWakeLock() {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    }

    if (state.isPlaying) {
      acquireWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      releaseWakeLock();
    };
  }, [state.isPlaying]);

  // Speak a single sentence
  const speakSentence = useCallback((index: number) => {
    if (index >= sentencesRef.current.length) {
      setState(prev => ({ ...prev, isPlaying: false }));
      onComplete?.();
      return;
    }

    const sentence = sentencesRef.current[index];
    const utterance = new SpeechSynthesisUtterance(sentence);
    utterance.rate = state.rate;
    utterance.pitch = 1;

    utterance.onend = () => {
      const nextIndex = index + 1;
      setState(prev => ({ ...prev, currentSentence: nextIndex }));
      onSentenceChange?.(nextIndex);

      if (nextIndex < sentencesRef.current.length) {
        speakSentence(nextIndex);
      } else {
        setState(prev => ({ ...prev, isPlaying: false }));
        onComplete?.();
      }
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setState(prev => ({ ...prev, isPlaying: false }));
    };

    utteranceRef.current = utterance;
    speechSynthesis.speak(utterance);
  }, [state.rate, onSentenceChange, onComplete]);

  // Play/resume
  const play = useCallback(() => {
    if (speechSynthesis.paused) {
      speechSynthesis.resume();
    } else {
      speakSentence(state.currentSentence);
    }
    setState(prev => ({ ...prev, isPlaying: true }));

    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'playing';
    }
  }, [state.currentSentence, speakSentence]);

  // Pause
  const pause = useCallback(() => {
    speechSynthesis.pause();
    setState(prev => ({ ...prev, isPlaying: false }));

    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'paused';
    }
  }, []);

  // Stop
  const stop = useCallback(() => {
    speechSynthesis.cancel();
    setState(prev => ({ ...prev, isPlaying: false, currentSentence: 0 }));

    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'none';
    }
  }, []);

  // Toggle play/pause
  const toggle = useCallback(() => {
    if (state.isPlaying) {
      pause();
    } else {
      play();
    }
  }, [state.isPlaying, play, pause]);

  // Skip forward
  const skipForward = useCallback(() => {
    const newIndex = Math.min(state.currentSentence + 3, sentencesRef.current.length - 1);
    speechSynthesis.cancel();
    setState(prev => ({ ...prev, currentSentence: newIndex }));
    onSentenceChange?.(newIndex);

    if (state.isPlaying) {
      speakSentence(newIndex);
    }
  }, [state.currentSentence, state.isPlaying, speakSentence, onSentenceChange]);

  // Skip backward
  const skipBackward = useCallback(() => {
    const newIndex = Math.max(state.currentSentence - 3, 0);
    speechSynthesis.cancel();
    setState(prev => ({ ...prev, currentSentence: newIndex }));
    onSentenceChange?.(newIndex);

    if (state.isPlaying) {
      speakSentence(newIndex);
    }
  }, [state.currentSentence, state.isPlaying, speakSentence, onSentenceChange]);

  // Set rate
  const setRate = useCallback((newRate: number) => {
    setState(prev => ({ ...prev, rate: newRate }));

    // If currently playing, restart with new rate
    if (state.isPlaying) {
      speechSynthesis.cancel();
      // Small delay to ensure cancel completes
      setTimeout(() => {
        speakSentence(state.currentSentence);
      }, 50);
    }
  }, [state.isPlaying, state.currentSentence, speakSentence]);

  // Seek to specific sentence
  const seekTo = useCallback((index: number) => {
    const validIndex = Math.max(0, Math.min(index, sentencesRef.current.length - 1));
    speechSynthesis.cancel();
    setState(prev => ({ ...prev, currentSentence: validIndex }));
    onSentenceChange?.(validIndex);

    if (state.isPlaying) {
      speakSentence(validIndex);
    }
  }, [state.isPlaying, speakSentence, onSentenceChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      speechSynthesis.cancel();
    };
  }, []);

  return {
    ...state,
    play,
    pause,
    stop,
    toggle,
    skipForward,
    skipBackward,
    setRate,
    seekTo,
    progress: state.totalSentences > 0
      ? (state.currentSentence / state.totalSentences) * 100
      : 0
  };
}
